import React, { useState, useEffect, useCallback } from 'react';
import { toast } from 'react-toastify';
import { gdriveGalleryAPI } from '../../services/api';
import useScrollAnimation from '../../hooks/useScrollAnimation';
import './Gallery.css';

// Auto-detect production environment and use appropriate API URL
const getApiUrl = () => {
    // If explicitly set in environment, use that
    if (process.env.REACT_APP_API_URL) {
        return process.env.REACT_APP_API_URL;
    }
    
    // In development, use localhost
    if (process.env.NODE_ENV === 'development') {
        return 'http://localhost:5000/api';
    }
    
    // In production, use NEW Render backend
    return 'https://nidharsanampublictrus.onrender.com/api';
};

const API_URL = getApiUrl();
const BASE_URL = API_URL.replace('/api', '');

console.log('🖼️ Gallery - API URL:', API_URL);
console.log('🖼️ Gallery - BASE URL:', BASE_URL);

// Sample gallery data with social work, teaching, and skills education
const sampleGalleryData = [
    { id: 1, title: 'Teaching Programming Skills', category: 'education', description: 'Students learning coding basics', color: '#FF6B6B', icon: 'fa-code' },
    { id: 2, title: 'Community Healthcare Camp', category: 'healthcare', description: 'Free health check-up drive', color: '#4ECDC4', icon: 'fa-hospital' },
    { id: 3, title: 'Digital Literacy Workshop', category: 'education', description: 'Teaching digital skills to youth', color: '#45B7D1', icon: 'fa-laptop' },
    { id: 4, title: 'Skill Development Program', category: 'education', description: 'Vocational training center', color: '#96CEB4', icon: 'fa-tools' },
    { id: 5, title: 'Community Outreach', category: 'community', description: 'Building stronger communities', color: '#FFEAA7', icon: 'fa-handshake' },
    { id: 6, title: 'Women Empowerment', category: 'community', description: 'Empowering women leaders', color: '#DDA0DD', icon: 'fa-venus' },
    { id: 7, title: 'Teaching Mathematics', category: 'education', description: 'Math skills for all ages', color: '#87CEEB', icon: 'fa-calculator' },
    { id: 8, title: 'Health Awareness', category: 'healthcare', description: 'Public health awareness campaign', color: '#FFB6C1', icon: 'fa-heart' },
    { id: 9, title: 'English Language Classes', category: 'education', description: 'Language learning program', color: '#98D8C8', icon: 'fa-book' },
    { id: 10, title: 'Social Welfare Program', category: 'community', description: 'Supporting underprivileged families', color: '#F7DC6F', icon: 'fa-people-carry' },
    { id: 11, title: 'Computer Literacy', category: 'education', description: 'Basic computer training', color: '#85C1E2', icon: 'fa-computer' },
    { id: 12, title: 'Entrepreneurship Workshop', category: 'education', description: 'Starting your own business', color: '#F8B88B', icon: 'fa-briefcase' },
    { id: 13, title: 'Counseling & Mentorship', category: 'community', description: 'Career guidance program', color: '#C39BD3', icon: 'fa-graduation-cap' },
    { id: 14, title: 'Healthcare Training', category: 'healthcare', description: 'First aid and health training', color: '#F5B7B1', icon: 'fa-ambulance' },
    { id: 15, title: 'Youth Development', category: 'community', description: 'Leadership building activities', color: '#A9DFBF', icon: 'fa-users' },
    { id: 16, title: 'Environmental Awareness', category: 'community', description: 'Green living initiatives', color: '#82E0AA', icon: 'fa-leaf' },
    { id: 17, title: 'Professional Training', category: 'education', description: 'Advanced skill development', color: '#85C1E9', icon: 'fa-certificate' },
    { id: 18, title: 'Mental Health Awareness', category: 'healthcare', description: 'Mental wellness programs', color: '#F1948A', icon: 'fa-brain' },
    { id: 19, title: 'Community Service', category: 'community', description: 'Volunteer activities', color: '#D5F4E6', icon: 'fa-hands-helping' },
    { id: 20, title: 'Financial Literacy', category: 'education', description: 'Money management skills', color: '#F9E79F', icon: 'fa-coins' }
];

const Gallery = () => {
    useScrollAnimation();
    const [images, setImages] = useState([]);
    const [loading, setLoading] = useState(true);
    const [selectedImage, setSelectedImage] = useState(null);
    const [carouselIndex, setCarouselIndex] = useState(0);
    const [selectedCategory, setSelectedCategory] = useState('all');
    const [viewMode, setViewMode] = useState('grid'); // 'grid' or 'masonry'
    const [searchTerm, setSearchTerm] = useState('');

    const fetchImages = useCallback(async () => {
        try {
            setLoading(true);
            const response = await gdriveGalleryAPI.getAll();
            if (response.data.success && response.data.data.length > 0) {
                setImages(response.data.data);
                console.log(`📸 Loaded ${response.data.data.length} images from Google Drive`);
            } else {
                setImages(sampleGalleryData);
                console.log(`📸 No images found in Google Drive, using sample data`);
            }
        } catch (error) {
            console.error('Error loading gallery:', error);
            setImages(sampleGalleryData);
            if (error.code === 'ERR_NETWORK' || error.message.includes('Network Error')) {
                toast.error('Unable to connect to server. Using sample images.', {
                    toastId: 'gallery-connection-error',
                    autoClose: 5000
                });
            } else if (error.response?.status === 500) {
                console.log('Server error: Using sample data instead');
            }
        } finally {
            setLoading(false);
        }
    }, []);

    useEffect(() => {
        fetchImages();
        // Auto-refresh every 30 seconds to get new uploads
        const interval = setInterval(fetchImages, 30000);
        return () => clearInterval(interval);
    }, [fetchImages]);
    
    // Auto-rotate carousel every 4 seconds
    useEffect(() => {
        if (images.length > 0) {
            const carouselInterval = setInterval(() => {
                setCarouselIndex((prev) => (prev + 1) % images.length);
            }, 4000);
            return () => clearInterval(carouselInterval);
        }
    }, [images.length]);

    const handleImageClick = (image) => {
        setSelectedImage(image);
    };

    const closeModal = () => {
        setSelectedImage(null);
    };

    // Get unique categories
    const categories = ['all', ...new Set(images.map(img => img.category).filter(Boolean))];

    // Filter images based on category and search
    const filteredImages = images.filter(image => {
        const matchesCategory = selectedCategory === 'all' || image.category === selectedCategory;
        const matchesSearch = !searchTerm || 
            (image.title?.toLowerCase().includes(searchTerm.toLowerCase()) ||
             image.name?.toLowerCase().includes(searchTerm.toLowerCase()) ||
             image.description?.toLowerCase().includes(searchTerm.toLowerCase()));
        return matchesCategory && matchesSearch;
    });

    if (loading) {
        return (
            <div className="gallery-loading">
                <div className="spinner"></div>
                <p>Loading Gallery...</p>
            </div>
        );
    }

    return (
        <div className="gallery-page">
            <div className="gallery-container">
                
                {/* Featured Carousel Section */}
                {images.length > 0 && (
                    <div className="featured-carousel">
                        <h2 className="carousel-title">Featured Gallery</h2>
                        <div className="carousel-wrapper">
                            {images.map((image, index) => {
                                const getImageSrc = () => {
                                    // Check for Google Drive image ID
                                    if (image.id && image.mimeType && image.mimeType.includes('image/')) {
                                        return `${BASE_URL}/api/gdrive-gallery/image/${image.id}`;
                                    }
                                    // Check for regular uploaded images
                                    if (image.url || image.thumbnail) return image.url || image.thumbnail;
                                    // Fallback to colored placeholder for sample data
                                    const color = image.color || '#667eea';
                                    return `data:image/svg+xml,%3Csvg xmlns='http://www.w3.org/2000/svg' viewBox='0 0 400 300'%3E%3Crect fill='${encodeURIComponent(color)}' width='400' height='300'/%3E%3C/svg%3E`;
                                };
                                
                                return (
                                    <div
                                        key={image._id || image.id}
                                        className={`carousel-slide ${index === carouselIndex ? 'active' : ''}`}
                                        onClick={() => handleImageClick(image)}
                                    >
                                        <img src={getImageSrc()} alt={image.title || image.name} />
                                        {!image.id && !image.url && !image.thumbnail && image.icon && (
                                            <div className="carousel-icon">
                                                <i className={`fas ${image.icon}`}></i>
                                            </div>
                                        )}
                                        <div className="carousel-info">
                                            {image.description && <p>{image.description}</p>}
                                        </div>
                                    </div>
                                );
                            })}
                             
                            {/* Carousel Navigation */}
                            <button 
                                className="carousel-nav prev" 
                                onClick={() => setCarouselIndex((prev) => prev === 0 ? images.length - 1 : prev - 1)}
                            >
                                <i className="fas fa-chevron-left"></i>
                            </button>
                            <button 
                                className="carousel-nav next" 
                                onClick={() => setCarouselIndex((prev) => (prev + 1) % images.length)}
                            >
                                <i className="fas fa-chevron-right"></i>
                            </button>
                            
                            {/* Carousel Indicators */}
                            <div className="carousel-indicators">
                                {images.map((_, index) => (
                                    <button
                                        key={index}
                                        className={`carousel-indicator ${index === carouselIndex ? 'active' : ''}`}
                                        onClick={() => setCarouselIndex(index)}
                                    />
                                ))}
                            </div>
                        </div>
                    </div>
                )}

                {images.length === 0 ? (
                    <div className="no-images">
                        <i className="fas fa-images"></i>
                        <h3>No images yet</h3>
                        <p>Upload your first photo to get started!</p>
                        <a href="/upload" className="upload-link">
                            Go to Upload Page
                        </a>
                    </div>
                ) : (
                    <>
                        {/* Filter and Controls */}
                        <div className="gallery-controls">
                            <div className="search-bar">
                                <i className="fas fa-search"></i>
                                <input 
                                    type="text" 
                                    placeholder="Search gallery..."
                                    value={searchTerm}
                                    onChange={(e) => setSearchTerm(e.target.value)}
                                />
                            </div>
                            
                            <div className="filter-buttons">
                                {categories.map(category => (
                                    <button
                                        key={category}
                                        className={`filter-btn ${selectedCategory === category ? 'active' : ''}`}
                                        onClick={() => setSelectedCategory(category)}
                                    >
                                        {category.charAt(0).toUpperCase() + category.slice(1)}
                                    </button>
                                ))}
                            </div>
                            
                            <div className="view-toggle">
                                <button 
                                    className={`view-btn ${viewMode === 'grid' ? 'active' : ''}`}
                                    onClick={() => setViewMode('grid')}
                                    title="Grid View"
                                >
                                    <i className="fas fa-th"></i>
                                </button>
                                <button 
                                    className={`view-btn ${viewMode === 'masonry' ? 'active' : ''}`}
                                    onClick={() => setViewMode('masonry')}
                                    title="Masonry View"
                                >
                                    <i className="fas fa-th-large"></i>
                                </button>
                            </div>
                        </div>

                        <h2 className="gallery-section-title">
                            {selectedCategory === 'all' ? 'All Photos' : selectedCategory.charAt(0).toUpperCase() + selectedCategory.slice(1)}
                            <span className="count">({filteredImages.length})</span>
                        </h2>
                        
                        <div className={`gallery-grid ${viewMode === 'masonry' ? 'masonry-view' : 'grid-view'}`}>
                            {filteredImages.map((image) => {
                            // Generate a SVG image for sample data without URLs
                            const getImageSrc = () => {
                                // Check for Google Drive image ID
                                if (image.id && image.mimeType && image.mimeType.includes('image/')) {
                                    return `${BASE_URL}/api/gdrive-gallery/image/${image.id}`;
                                }
                                // Check for regular uploaded images
                                if (image.url || image.thumbnail) {
                                    return image.url || image.thumbnail;
                                }
                                // Create a simple colored SVG for sample data
                                const color = image.color || '#667eea';
                                return `data:image/svg+xml,%3Csvg xmlns='http://www.w3.org/2000/svg' viewBox='0 0 400 300'%3E%3Crect fill='${encodeURIComponent(color)}' width='400' height='300'/%3E%3C/svg%3E`;
                            };

                            return (
                                <div
                                    key={image._id || image.id}
                                    className="gallery-item scroll-animate-scale"
                                    onClick={() => handleImageClick(image)}
                                    style={!image.id && !image.url && !image.thumbnail && image.color ? { position: 'relative' } : {}}
                                >
                                    <img
                                        src={getImageSrc()}
                                        alt={image.title || image.name}
                                        loading="lazy"
                                    />
                                    {!image.id && !image.url && !image.thumbnail && image.icon && (
                                        <div style={{
                                            position: 'absolute',
                                            top: '50%',
                                            left: '50%',
                                            transform: 'translate(-50%, -50%)',
                                            pointerEvents: 'none',
                                            zIndex: 1,
                                            fontSize: '3rem',
                                            color: 'white',
                                            textShadow: '0 2px 8px rgba(0,0,0,0.3)'
                                        }}>
                                            <i className={`fas ${image.icon}`}></i>
                                        </div>
                                    )}
                                    <div className="image-overlay">
                                        {image.category && (
                                            <span className="category-badge">
                                                {image.category}
                                            </span>
                                        )}
                                    </div>
                                </div>
                            );
                        })}
                        </div>
                    </>
                )}
            </div>

            {selectedImage && (
                <div className="image-modal" onClick={closeModal}>
                    <div className="modal-content" onClick={(e) => e.stopPropagation()}>
                        <button className="close-modal" onClick={closeModal}>
                            <i className="fas fa-times"></i>
                        </button>
                        <div className="modal-image-container">
                            <img
                                src={
                                    selectedImage.id && selectedImage.mimeType && selectedImage.mimeType.includes('image/') 
                                        ? `${BASE_URL}/api/gdrive-gallery/image/${selectedImage.id}`
                                        : selectedImage.url || selectedImage.thumbnail || (selectedImage.color ? `data:image/svg+xml,%3Csvg xmlns='http://www.w3.org/2000/svg' viewBox='0 0 400 300'%3E%3Crect fill='${encodeURIComponent(selectedImage.color)}' width='400' height='300'/%3E%3C/svg%3E` : '')
                                }
                                alt={selectedImage.title || selectedImage.name}
                            />
                            {!selectedImage.id && !selectedImage.url && !selectedImage.thumbnail && selectedImage.icon && (
                                <div className="modal-icon-overlay">
                                    <i className={`fas ${selectedImage.icon}`}></i>
                                </div>
                            )}
                        </div>
                        <div className="modal-info">
                            {selectedImage.description && <p>{selectedImage.description}</p>}
                            {selectedImage.category && (
                                <span className="category-badge">
                                    {selectedImage.category}
                                </span>
                            )}
                        </div>
                    </div>
                </div>
            )}
        </div>
    );
};

export default Gallery;
